import { indexes } from '@mab-common/utils'

import type {
  MsearchMultisearchBody,
  MsearchMultiSearchItem,
  QueryDslQueryContainer,
} from '@elastic/elasticsearch/lib/api/types'

// Common English stop words that add noise to search results
const STOP_WORDS = new Set([
  'and',
  'or',
  'but',
  'the',
  'a',
  'an',
  'of',
  'in',
  'on',
  'at',
  'to',
  'for',
  'with',
  'by',
  'from',
  'as',
  'is',
  'was',
  'are',
  'were',
])

/**
 * Returns optimal fuzziness setting based on search term length
 * This reduces false matches for very short search terms
 */
function getFuzziness(searchTerm: string): number | 'AUTO' {
  const term = searchTerm.trim()
  if (term.length <= 3) return 0 // No fuzziness for 1-3 chars
  if (term.length <= 6) return 1 // Low fuzziness for 4-6 chars
  return 2 // Standard fuzziness only for 7+ chars
}

/**
 * Removes stop words from search terms
 * @param searchTerm - The original search term
 * @param alwaysRemove - If true, removes stop words regardless of length (for artist fields)
 */
function removeStopWords(searchTerm: string, alwaysRemove = false): string {
  const words = searchTerm.toLowerCase().trim().split(/\s+/)

  // Only remove stop words if:
  // 1. alwaysRemove is true (for artist fields), OR
  // 2. There are 3+ words in the search
  if (!alwaysRemove && words.length < 3) {
    return searchTerm
  }

  // Remove stop words
  const filtered = words.filter(word => !STOP_WORDS.has(word))

  // If we removed everything, return original
  return filtered.length > 0 ? filtered.join(' ') : searchTerm
}

import type { ArticlePreviewCardProps } from '~/components/atoms/ArticlePreviewCard/ArticlePreviewCard'
import type { ArtworkCardProps } from '~/components/atoms/ArtworkCard/ArtworkCard'
import {
  type ProfileImageProps,
  ProfileImageType,
} from '~/components/atoms/ProfileImage/ProfileImage'
import type { SearchAbleSelectProps } from '~/components/atoms/SearchAbleSelect/SearchAbleSelect'
import RecordTypes = indexes.RecordTypes

const getArtistSearchQuery: any = (searchTerm: string) => {
  return {
    function_score: {
      query: {
        bool: {
          should: [
            {
              multi_match: {
                query: searchTerm,
                fields: [
                  'suggestText',
                  'suggestText._2gram',
                  'suggestText._3gram',
                ],
                type: 'bool_prefix',
                boost: 2,
              },
            },
            {
              multi_match: {
                query: searchTerm,
                fields: ['suggestText'],
                fuzziness: 'AUTO',
                type: 'best_fields',
              },
            },
            // Add prefix matching on name field for cases like "yoshi" -> "Yoshitomo Nara"
            {
              match_phrase_prefix: {
                name: {
                  query: searchTerm,
                  boost: 1.5,
                },
              },
            },
          ],
        },
      },
      functions: [
        {
          field_value_factor: {
            field: 'tierLevel',
            modifier: 'reciprocal',
            factor: 5,
            missing: 5,
          },
        },
      ],
      boost_mode: 'multiply',
    },
  }
}

export const buildElasticSearchTerm = (
  type: RecordTypes,
  _searchTerm?: string
): QueryDslQueryContainer[] | QueryDslQueryContainer => {
  const originalSearchTerm = (_searchTerm || '')?.trim().toLowerCase()
  if (!originalSearchTerm) return []

  // Remove stop words for searches with 3+ words
  const searchTerm = removeStopWords(originalSearchTerm, false)

  switch (type) {
    case RecordTypes.PODCASTS_AND_VIDEOS:
      return [
        {
          bool: {
            must: [
              {
                multi_match: {
                  query: searchTerm,
                  fields: [
                    'title^2',
                    'keywords^1.8',
                    'artistSlug^1.5',
                    'author^1.2',
                    'articleSummary^1.1',
                    'customTaxonomy',
                    'image.credit.text',
                  ],
                },
              },
              {
                bool: {
                  should: [
                    { term: { 'articleTypeSlug.keyword': 'videos' } },
                    { term: { 'articleTypeSlug.keyword': 'podcasts' } },
                  ],
                },
              },
            ],
          },
        },
      ]
    case RecordTypes.VIDEOS:
      return [
        {
          bool: {
            must: [
              {
                multi_match: {
                  query: searchTerm,
                  fields: [
                    'title^2',
                    'keywords^1.8',
                    'artistSlug^1.5',
                    'author^1.2',
                    'articleSummary^1.1',
                    'customTaxonomy',
                    'image.credit.text',
                  ],
                },
              },
              {
                bool: {
                  should: [{ term: { 'articleTypeSlug.keyword': 'videos' } }],
                },
              },
            ],
          },
        },
      ]
    case RecordTypes.PODCASTS:
      return [
        {
          bool: {
            must: [
              {
                multi_match: {
                  query: searchTerm,
                  fields: [
                    'title^2',
                    'keywords^1.8',
                    'artistSlug^1.5',
                    'author^1.2',
                    'articleSummary^1.1',
                    'customTaxonomy',
                    'image.credit.text',
                  ],
                },
              },
              {
                bool: {
                  should: [{ term: { 'articleTypeSlug.keyword': 'podcasts' } }],
                },
              },
            ],
          },
        },
      ]
    case RecordTypes.ARTICLES:
      return [
        {
          bool: {
            must: [
              {
                multi_match: {
                  query: searchTerm,
                  fields: [
                    'title^2',
                    'keywords^1.8',
                    'artistSlug^1.5',
                    'author^1.2',
                    'articleSummary^1.1',
                    'customTaxonomy',
                    'image.credit.text',
                  ],
                },
              },
            ],
            must_not: [
              {
                terms: {
                  'articleTypeSlug.keyword': ['videos', 'podcasts'],
                },
              },
            ],
          },
        },
      ]
    case RecordTypes.ARTIST:
      return getArtistSearchQuery(searchTerm)
    case RecordTypes.TICKETS:
      return [
        {
          multi_match: {
            query: searchTerm,
            fields: [
              'artist^2',
              'title^1.8',
              'artworkId^1.5',
              'collectionSlug',
            ],
          },
        },
      ]
    case RecordTypes.ARTIST_SELECT_LIST:
      return getArtistSearchQuery(searchTerm)
    case RecordTypes.ARTWORK_SELECT_LIST:
      return [
        {
          bool: {
            should: [
              {
                multi_match: {
                  query: searchTerm,
                  fields: [
                    'suggestText',
                    'suggestText._2gram',
                    'suggestText._3gram',
                  ],
                  type: 'bool_prefix',
                  boost: 2,
                },
              },
              {
                multi_match: {
                  query: searchTerm,
                  fields: [
                    'suggestText',
                    'suggestText._2gram',
                    'suggestText._3gram',
                  ],
                  fuzziness: getFuzziness(searchTerm),
                  type: 'best_fields',
                },
              },
            ],
          },
        },
      ]
    case RecordTypes.ALL_ARTWORK_SELECT_LIST: {
      // For artist field, always remove stop words
      const artistSearchTerm = removeStopWords(originalSearchTerm, true)

      return {
        function_score: {
          query: {
            bool: {
              should: [
                // Highest priority: Exact phrase match
                {
                  match_phrase: {
                    suggestText: {
                      query: searchTerm,
                      boost: 10, // Exact phrases should win
                    },
                  },
                },
                // Second priority: Title with smart typo tolerance
                {
                  multi_match: {
                    query: searchTerm,
                    fields: [
                      'suggestText',
                      'suggestText._2gram',
                      'suggestText._3gram',
                    ],
                    type: 'best_fields',
                    fuzziness: 'AUTO', // Smart fuzziness based on word length
                    prefix_length: 2, // First 2 chars must match
                    boost: 5,
                  },
                },
                // Third priority: Prefix matching for autocomplete
                {
                  multi_match: {
                    query: searchTerm,
                    fields: [
                      'suggestText',
                      'suggestText._2gram',
                      'suggestText._3gram',
                    ],
                    type: 'bool_prefix',
                    boost: 2,
                  },
                },
                // Format matching
                {
                  multi_match: {
                    query: searchTerm,
                    fields: ['format.name'],
                    type: 'best_fields',
                  },
                },
                // Artist field with stop words ALWAYS removed
                {
                  multi_match: {
                    query: artistSearchTerm,
                    fields: ['artist'],
                    type: 'best_fields',
                    boost: 1.5, // Reduced from 3
                    fuzziness: getFuzziness(artistSearchTerm), // Standard AUTO: allows typos on 3+ char words
                    prefix_length: 2, // First 2 chars must match
                  },
                },
              ],
            },
          },
          functions: [
            {
              field_value_factor: {
                field: 'tierLevel',
                modifier: 'reciprocal',
                factor: 5,
                missing: 5,
              },
            },
          ],
          boost_mode: 'multiply',
        },
      }
    }
    case RecordTypes.COLLECTION:
      return [
        {
          bool: {
            should: [
              {
                prefix: {
                  title: {
                    value: searchTerm,
                  },
                },
              },
              {
                match: {
                  title: searchTerm,
                },
              },
              {
                match: {
                  artist: searchTerm,
                },
              },
              {
                fuzzy: {
                  title: {
                    value: searchTerm,
                    fuzziness: getFuzziness(searchTerm),
                  },
                },
              },
            ],
          },
        },
      ]
    case RecordTypes.ARTWORK:
    default: {
      // For artist field, always remove stop words
      const artistSearchTerm = removeStopWords(originalSearchTerm, true)

      return {
        function_score: {
          query: {
            bool: {
              should: [
                // Highest priority: Exact phrase match (use original with stop words)
                {
                  match_phrase: {
                    suggestText: {
                      query: originalSearchTerm,
                      boost: 10, // Exact phrases should win
                    },
                  },
                },
                // Second priority: Title with smart typo tolerance
                {
                  multi_match: {
                    query: searchTerm,
                    fields: [
                      'suggestText',
                      'suggestText._2gram',
                      'suggestText._3gram',
                    ],
                    type: 'best_fields',
                    fuzziness: 'AUTO', // Smart fuzziness based on word length
                    prefix_length: 2, // First 2 chars must match
                    boost: 5,
                  },
                },
                // Third priority: Prefix matching for autocomplete
                {
                  multi_match: {
                    query: searchTerm,
                    fields: [
                      'suggestText',
                      'suggestText._2gram',
                      'suggestText._3gram',
                    ],
                    type: 'bool_prefix',
                    boost: 2,
                  },
                },
                // Format matching
                {
                  multi_match: {
                    query: searchTerm,
                    fields: ['format.name'],
                    type: 'best_fields',
                  },
                },
                // Artist field with stop words ALWAYS removed
                {
                  multi_match: {
                    query: artistSearchTerm,
                    fields: ['artist'],
                    type: 'best_fields',
                    boost: 1.5, // Reduced from 3
                    fuzziness: getFuzziness(artistSearchTerm), // Standard AUTO: allows typos on 3+ char words
                    prefix_length: 2, // First 2 chars must match
                  },
                },
              ],
            },
          },
          functions: [
            {
              field_value_factor: {
                field: 'tierLevel',
                modifier: 'reciprocal',
                factor: 5,
                missing: 5,
              },
            },
          ],
          boost_mode: 'multiply',
        },
      }
    }
  }
}

export const buildElasticSortFields = (
  type: RecordTypes,
  artistArtworks?: boolean
): MsearchMultisearchBody['sort'] => {
  switch (type) {
    case RecordTypes.ARTIST:
      return [
        {
          _score: {
            order: 'desc',
          },
        },
      ]
    case RecordTypes.ARTWORK:
      if (artistArtworks) {
        return [
          {
            _score: {
              order: 'desc',
            },
          },
          {
            sellTicketsCount: {
              order: 'desc',
            },
          },
          {
            indexWeighting: {
              order: 'asc',
            },
          },
        ]
      } else {
        return [
          {
            _score: {
              order: 'desc',
            },
          },
          {
            indexWeighting: {
              order: 'asc',
            },
          },
          {
            indexOrderDate: {
              order: 'desc',
            },
          },
        ]
      }
    case RecordTypes.COLLECTION:
      return [
        {
          _score: {
            order: 'desc',
          },
        },
        {
          isFeatured: {
            order: 'desc',
          },
        },
      ]
    case RecordTypes.LATEST_ARTWORK:
      return [
        {
          indexWeighting: {
            order: 'asc',
          },
        },
        {
          indexOrderDate: {
            order: 'desc',
          },
        },
        {
          romeoDateModified: {
            order: 'desc',
          },
        },
      ]
    case RecordTypes.ARTWORK_SELECT_LIST:
    case RecordTypes.ALL_ARTWORK_SELECT_LIST:
      return [
        {
          _score: {
            order: 'desc',
          },
        },
      ]
    case RecordTypes.PODCASTS_AND_VIDEOS:
    case RecordTypes.PODCASTS:
    case RecordTypes.VIDEOS:
    case RecordTypes.ARTICLES:
      return [
        {
          _score: {
            order: 'desc',
          },
        },
        {
          documentPriority: {
            order: 'desc',
            missing: '_last', // Ensures documents missing the 'documentPriority' field appear last
          },
        },
        {
          publishDate: {
            order: 'desc',
          },
        },
      ]
    case RecordTypes.ARTICLES_INDEX:
      return [
        {
          publishDate: {
            order: 'desc',
          },
        },
      ]
    case RecordTypes.TICKETS:
      return [
        {
          dateModified: {
            order: 'desc',
          },
        },
      ]
    case RecordTypes.ARTIST_SELECT_LIST:
      return [
        {
          _score: {
            order: 'desc',
          },
        },
        {
          'name.keyword': {
            order: 'asc',
          },
        },
      ]
    default:
      return undefined
  }
}

export const buildElasticResultSourceFields = (resultType: RecordTypes) => {
  switch (resultType) {
    case RecordTypes.ARTWORK:
      return [
        'objectID',
        'artist',
        'artistImage',
        'artistSlug',
        'artistUrl',
        'artistId',
        'artworkSlug',
        'collectionSlug',
        'collectionTaxonomy',
        'format',
        'image',
        'signed',
        'title',
        'url',
        'poa',
        'listPriceMin',
        'listPriceMax',
        'listPriceMinValues',
        'listPriceMaxValues',
        'aagr',
        'buyTicketsCount',
        'sellTicketsCount',
        'ownedTicketsCount',
      ]
    case RecordTypes.ARTIST:
      return ['name', 'image', 'slug', 'totalArtworks', 'tierLevel']
    case RecordTypes.PODCASTS_AND_VIDEOS:
    case RecordTypes.PODCASTS:
    case RecordTypes.VIDEOS:
    case RecordTypes.ARTICLES:
      return [
        'objectID',
        'title',
        'subtitle',
        'slug',
        'image',
        'listPreviewImage',
        'articleSummary',
        'artistSlug',
        'customTaxonomy',
        'articleType',
        'articleTypeSlug',
        'articleTypeTitle',
        'url',
        'secondaryCategories',
        'author',
        'authorSlug',
        'authorImage',
        'readtime',
        'publishDate',
      ]
    case RecordTypes.COLLECTION:
      return ['title', 'image', 'url', 'artist']
    case RecordTypes.PROFILE_IMAGE:
      return ['title', 'image', 'objectID', 'url', 'format']
    case RecordTypes.ARTWORK_SELECT_LIST:
    case RecordTypes.ALL_ARTWORK_SELECT_LIST:
      return ['objectID', 'format', 'image', 'title']
    case RecordTypes.ARTIST_SELECT_LIST:
      return ['objectID', 'name', 'slug', 'image']
    default:
      return undefined
  }
}

export type TBuildElasticToHit = {
  type: string
  total: number
  hits: Array<{ __count?: number; __key?: string; [x: string]: any } | any>
}

export const buildAggsCountField = (field: RecordTypes | string) =>
  `${field}_count`

export const buildElasticAggsToHits = (
  response: MsearchMultiSearchItem
): Array<TBuildElasticToHit> => {
  if (!response?.aggregations) return []
  return Object.keys(response.aggregations).reduce((acc, curr) => {
    if (!curr.endsWith('_count')) {
      const aggs: any = response.aggregations || {}
      acc.push({
        type: curr,
        total: aggs?.[`${curr}_count`]?.['value'] ?? 0,
        hits: (aggs[curr]?.['buckets'] || []).flatMap((each: any) => {
          return (each?.['docs']?.['hits']?.['hits'] || [])?.map((h: any) => {
            return {
              ...(h?.['_source'] || {}),
              __key: each.key,
              __count: each?.['docs']?.['hits']?.['total']?.['value'] || 0,
            }
          })
        }),
      })
    }
    return acc
  }, [] as Array<TBuildElasticToHit>)
}
export const buildElasticAggsToBuckets = (
  response: MsearchMultiSearchItem
): Array<TBuildElasticToHit> => {
  if (!response?.aggregations) return []
  return Object.keys(response.aggregations).reduce((acc, curr) => {
    if (!curr.endsWith('_count')) {
      const aggs: any = response.aggregations || {}
      acc.push({
        type: curr,
        total: 0,
        hits: (aggs[curr]?.['buckets'] || []).flatMap((each: any) => {
          return {
            __key: each.key,
            __count: each.doc_count ?? 0,
          }
        }),
      })
    }
    return acc
  }, [] as Array<TBuildElasticToHit>)
}
export const buildElasticToHits = (
  key: string,
  response: MsearchMultiSearchItem
): Array<TBuildElasticToHit> => {
  if (!response?.hits) return []
  return [
    {
      type: key,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      total: response?.hits?.total?.['value'] ?? 0,
      hits: response?.hits?.hits?.map(h => h['_source'] || {}),
    },
  ]
}

const buildArtistProfileImage = (data: any): ProfileImageProps[] => {
  if (!Array.isArray(data) || !data?.length) return []
  return data.map((result: any) => {
    const item: ProfileImageProps = {
      _id: result._id,
      name: result.name,
      image: result.image,
      qualifiedUrl: `artist-${result.slug}`,
      type: ProfileImageType.Artist,
      subtitle: result.format?.name,
      totalArtworks: result.totalArtworks,
      tierLevel: result.tierLevel,
    }
    return item
  })
}
const buildCollectionProfileImage = (data: any): ProfileImageProps[] => {
  if (!Array.isArray(data) || !data?.length) return []
  return data.map((result: any) => {
    const item: ProfileImageProps = {
      _id: result.collectionId,
      name: result.title,
      image: result.image,
      qualifiedUrl: result.url,
      type: ProfileImageType.Artwork,
      subtitle: result.artist,
      totalArtworks: result?.collectionCount || result?.[`__count`] || 0,
    }
    return item
  })
}
const buildProfileImage = (data: any): ProfileImageProps[] => {
  if (!Array.isArray(data) || !data?.length) return []
  return data.map((result: any) => {
    const item: ProfileImageProps = {
      _id: result.objectID,
      name: result.title,
      image: result.image,
      qualifiedUrl: result.url,
      type: ProfileImageType.Artwork,
      subtitle: result.format?.name,
      totalArtworks: result?.[`__count`] ?? 0,
    }
    return item
  })
}
const buildArticleCard = (data: any): ArticlePreviewCardProps[] => {
  if (!Array.isArray(data) || !data?.length) return []

  return data.map((result: any) => {
    const item: ArticlePreviewCardProps = {
      _id: result.objectID,
      title: result.title,
      subtitle: result.subtitle,
      slug: result.slug,
      leadImage: result.image,
      listPreviewImage: result?.listPreviewImage,
      summary: result.articleSummary,
      artistSlug: result.artistSlug,
      articleTaxonomy: result.customTaxonomy,
      modules: result.modules, //this doesn't exist in elastic
      articleType: result.articleType,
      articleTypeSlug: result.articleTypeSlug,
      type: 'article',
      articleTypeTitle: result.articleTypeTitle,
      qualifiedUrl: result.url,
      secondaryCategories: result.secondaryCategories,
      readtime: result?.readtime,
      publishDate: result?.publishDate,
      author: {
        name: result?.author,
        slug: result?.authorSlug,
        image: result?.authorImage,
      },
    }
    return item
  })
}
const buildArtworkCard = (data: any): ArtworkCardProps[] => {
  if (!Array.isArray(data) || !data?.length) return []
  return data.map((result: any) => {
    const item: ArtworkCardProps = {
      _id: result.objectID,
      artist: result.artist || '',
      artistImage: result.artistImage || '',
      artistSlug: result.artistSlug || '',
      artistUrl: result.artistUrl || '',
      artistId: result.artistId || '',
      artworkSlug: result.artworkSlug || '',
      collectionSlug: result.collectionSlug || '',
      collectionTaxonomy: result.collectionTaxonomy || '',
      format: result.format?.name || '',
      image: result.image || '',
      signed: result.signed || '',
      title: result.title || '',
      qualifiedUrl: result.url || '',
      poa: result.poa,
      listPriceMin: result.listPriceMin,
      listPriceMax: result.listPriceMax,
      listPriceMinValues: result.listPriceMinValues,
      listPriceMaxValues: result.listPriceMaxValues,
      aagr: result.aagr,
      buyTicketsCount: result.buyTicketsCount,
      sellTicketsCount: result.sellTicketsCount,
      ownedTicketsCount: result.ownedTicketsCount,
    }
    return item
  })
}
export const buildSelectBox = (
  data: any,
  type:
    | RecordTypes.ARTIST_SELECT_LIST
    | RecordTypes.ARTWORK_SELECT_LIST
    | RecordTypes.ALL_ARTWORK_SELECT_LIST
): SearchAbleSelectProps['options'] => {
  if (!Array.isArray(data) || !data?.length) return []
  return data.map((result: any) => {
    const item: SearchAbleSelectProps['options'][number] = {
      value: result.objectID,
      meta: {
        secondTitle: RecordTypes.ALL_ARTWORK_SELECT_LIST
          ? result.artist || ''
          : undefined,
        thirdTitle: RecordTypes.ALL_ARTWORK_SELECT_LIST
          ? result?.signed
            ? `${result.signed} Print`
            : ''
          : undefined,
      },
      label:
        type === RecordTypes.ARTIST_SELECT_LIST
          ? result.name
          : `${result.title}`,
      image: result.image,
    }
    return item
  })
}

export const buildFormatFacet = (elasticHit: TBuildElasticToHit) => {
  return {
    type: elasticHit.type,
    total: elasticHit.total,
    facets: elasticHit?.hits?.map(hit => ({
      count: hit.__count,
      name: hit?.format?.name,
      slug: hit?.format?.slug,
      _id: hit?.format?._id,
    })),
  }
}
export const buildArtistFacet = (elasticHit: TBuildElasticToHit) => {
  return {
    type: elasticHit.type,
    total: elasticHit.total,
    facets: elasticHit?.hits?.map(hit => ({
      totalArtworks: hit.__count,
      name: hit?.artist,
      slug: hit?.artistSlug,
      _id: hit?.artistId,
    })),
  }
}
export const buildGenreFacet = (elasticHit: TBuildElasticToHit) => {
  return {
    type: elasticHit.type,
    total: elasticHit.total,
    facets: elasticHit?.hits?.map(hit => {
      const genre = hit.genres?.find((f: any) => f._id === hit.__key)
      return {
        count: hit.__count,
        name: genre?.name,
        slug: genre?.slug,
        _id: genre?._id,
      }
    }),
  }
}
export const buildPriceFacet = (elasticHit: TBuildElasticToHit) => {
  return {
    type: elasticHit.type,
    total: elasticHit.total,
    facets: elasticHit?.hits?.map(hit => `${Math.round(+(hit.__key || 0))}`),
  }
}

export const buildMappedElasticHits = (
  hits: Array<TBuildElasticToHit>,
  dataKey: 'hits' | 'data' = 'hits',
  recordType?: RecordTypes
) => {
  return hits
    .map(build => {
      const data = structuredClone(build.hits)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      delete build.hits

      const _recordType = recordType || build.type
      switch (_recordType) {
        case RecordTypes.ARTIST:
          return {
            ...build,
            [dataKey]: buildArtistProfileImage(data),
            sl: 1,
            insightsQueryID: undefined,
          }
        case RecordTypes.PROFILE_IMAGE:
          return {
            ...build,
            [dataKey]: buildProfileImage(data),
            sl: 2,
            insightsQueryID: undefined,
          }
        case RecordTypes.ARTWORK:
        case RecordTypes.LATEST_ARTWORK:
        case RecordTypes.RELATED_ARTWORK:
          return {
            ...build,
            [dataKey]: buildArtworkCard(data),
            sl: 3,
            insightsQueryID: undefined,
          }
        case RecordTypes.COLLECTION:
          return {
            ...build,
            [dataKey]: buildCollectionProfileImage(data),
            sl: 4,
            insightsQueryID: undefined,
          }
        case RecordTypes.PODCASTS_AND_VIDEOS:
        case RecordTypes.PODCASTS:
        case RecordTypes.VIDEOS:
        case RecordTypes.ARTICLES:
          return {
            ...build,
            [dataKey]: buildArticleCard(data),
            sl: 5,
            insightsQueryID: undefined,
          }
        case RecordTypes.ALL_ARTWORK_SELECT_LIST:
          return {
            ...build,
            [dataKey]: buildSelectBox(
              data,
              RecordTypes.ALL_ARTWORK_SELECT_LIST
            ),
            sl: 6,
            insightsQueryID: undefined,
          }
        case RecordTypes.ARTWORK_SELECT_LIST:
          return {
            ...build,
            [dataKey]: buildSelectBox(data, RecordTypes.ARTWORK_SELECT_LIST),
            sl: 6,
            insightsQueryID: undefined,
          }
        case RecordTypes.ARTIST_SELECT_LIST:
          return {
            ...build,
            [dataKey]: buildSelectBox(data, RecordTypes.ARTIST_SELECT_LIST),
            sl: 7,
            insightsQueryID: undefined,
          }
        default:
          return {
            ...build,
            [dataKey]: data,
            sl: 8,
            insightsQueryID: undefined,
          }
      }
    })
    .sort((a, b) => a.sl - b.sl)
}
